import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Input, 
  Modal, 
  Form, 
  Select, 
  Popconfirm, 
  message, 
  Tag,
  Typography,
  Card,
  Row,
  Col,
  Divider,
  Upload,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  UserOutlined,
  MailOutlined,
  ImportOutlined,
  UsergroupAddOutlined,
  UploadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { getStudents, addStudent, updateStudent, deleteStudent } from '../../api/students';
import { getAvailableStudents, addStudentToTeacher } from '../../api/teacherStudentRelations';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const StudentManager = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingStudent, setEditingStudent] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [availableStudents, setAvailableStudents] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  // 年级选项
  const gradeOptions = [
    '一年级', '二年级', '三年级', '四年级', '五年级', '六年级',
    '初一', '初二', '初三', '高一', '高二', '高三'
  ];

  // 获取学生列表
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const data = await getStudents();
      setStudents(Array.isArray(data) ? data : []); // 确保设置的是数组
    } catch (error) {
      console.error('获取学生列表失败:', error);
      message.error('获取学生列表失败');
      setStudents([]); // 发生错误时设置空数组
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  // 获取可用学生列表
  const fetchAvailableStudents = async () => {
    try {
      setLoadingAvailable(true);
      const data = await getAvailableStudents();
      setAvailableStudents(data || []);
    } catch (error) {
      console.error('获取可用学生列表失败:', error);
      message.error('获取可用学生列表失败');
    } finally {
      setLoadingAvailable(false);
    }
  };

  // 处理添加/编辑学生
  const handleAddOrUpdateStudent = async (values) => {
    try {
      const studentData = {
        ...values,
        role: 'student',
        is_active: values.is_active === '1' || values.is_active === true,
      };

      console.log('准备提交的学生数据:', studentData);

      // 编辑模式
      if (editingStudent) {
        const { password, ...updateData } = studentData; // 更新时不传递密码
        await updateStudent(editingStudent.id, updateData);
        message.success('学生信息更新成功');
        resetFormAndRefresh();
        return;
      }

      // 创建模式
      validateRequiredFields(studentData);
      const studentId = await createStudentAndGetId(studentData);

      if (studentId) {
        await associateStudentWithTeacher(studentId);
      } else {
        message.success('学生添加成功，但需要手动关联到您的账户');
      }

      resetFormAndRefresh();
    } catch (error) {
      handleError(error);
    }
  };

  /* ------------------------ 辅助函数 ------------------------ */
  const validateRequiredFields = (data) => {
    if (!data.username) throw new Error('用户名不能为空');
    if (!data.password) throw new Error('密码不能为空');
    if (!data.name) throw new Error('姓名不能为空');
  };

  const createStudentAndGetId = async (studentData) => {
    const response = await addStudent(studentData);
    console.log('添加学生响应:', response);
    let studentId = null;

    if (typeof response === 'object') {
      if (response.data) {
        studentId = typeof response.data === 'object'
          ? (response.data.id || response.data.userId || response.data.user_id)
          : (typeof response.data === 'number' ? response.data : null);
      } else {
        studentId = response.id || response.userId || response.user_id;
      }
    } else if (typeof response === 'number') {
      studentId = response;
    }

    console.log('提取的学生 ID:', studentId);
    return studentId;
  };

  const associateStudentWithTeacher = async (studentId) => {
    try {
      const numericStudentId = Number(studentId);
      if (isNaN(numericStudentId)) throw new Error('学生 ID 格式无效');

      const success = await createTeacherStudentRelation(numericStudentId);
      if (success) {
        message.success('学生添加成功并已关联到您的账户');
        delayedRefresh();
        return;
      }
      await tryBackupAssociationMethod(numericStudentId);
    } catch (err) {
      logErrorDetails(err);
      message.warning('学生创建成功，但关联到您的账户失败。您可以稍后手动关联。');
    }
  };

  const tryBackupAssociationMethod = async (studentId) => {
    try {
      const availableStudents = await getAvailableStudents();
      const found = availableStudents.find((s) => s.id === studentId);
      if (found) {
        await addStudentToTeacher(studentId);
        message.success('学生已成功关联到您的账户');
        delayedRefresh();
      } else {
        message.warning('未能在可用学生列表中找到该学生，请稍后手动关联');
      }
    } catch (e) {
      logErrorDetails(e);
      message.warning('学生创建成功，但关联到您的账户失败。请稍后手动关联。');
    }
  };

  const delayedRefresh = () => setTimeout(fetchStudents, 500);

  const resetFormAndRefresh = () => {
    form.resetFields();
    setModalVisible(false);
    setEditingStudent(null);
    fetchStudents();
  };

  const handleError = (error) => {
    console.error('保存学生信息失败:', error);
    let msg = '保存学生信息失败';
    if (error.response?.data?.message) msg += `: ${error.response.data.message}`;
    else if (error.message) msg += `: ${error.message}`;
    message.error(msg);
  };

  const logErrorDetails = (error) => {
    if (error.response) {
      console.error('错误状态码:', error.response.status);
      console.error('错误数据:', error.response.data);
    }
  };

  // 处理删除学生
  const handleDeleteStudent = async (id) => {
    try {
      await deleteStudent(id);
      message.success('学生删除成功');
      fetchStudents();
    } catch (error) {
      console.error('删除学生失败:', error);
      message.error('删除学生失败');
    }
  };

  // 打开编辑模态框
  const showEditModal = (student) => {
    setEditingStudent(student);
    form.setFieldsValue({
      username: student.username,
      name: student.name,
      email: student.email,
      grade_level: student.grade_level,
      is_active: student.is_active ? '1' : '0'
    });
    setModalVisible(true);
  };

  // 打开添加模态框
  const showAddModal = () => {
    setEditingStudent(null);
    form.resetFields();
    setModalVisible(true);
  };

  // 显示导入模态框
  const showImportModal = () => {
    setImportModalVisible(true);
    fetchAvailableStudents();
    setSelectedStudentIds([]);
  };
  
  // 直接创建教师学生关联记录
  const createTeacherStudentRelation = async (studentId) => {
    try {
      console.log('尝试直接创建教师学生关联记录，学生 ID:', studentId);
      
      // 获取当前用户信息
      const userJson = localStorage.getItem('user');
      const user = userJson ? JSON.parse(userJson) : null;
      
      if (!user || !user.id) {
        console.error('无法获取当前教师ID');
        return false;
      }
      
      const teacherId = user.id;
      console.log('当前教师 ID:', teacherId);
      
      // 获取认证令牌
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('无法获取认证令牌');
        return false;
      }
      
      // 发送请求创建关联
      const response = await axios.post(
        `${API_BASE_URL}/teacher/students`,
        { 
          studentId: Number(studentId),
          teacher_id: teacherId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('创建教师学生关联响应:', response.data);
      return true;
    } catch (error) {
      console.error('创建教师学生关联失败:', error);
      if (error.response) {
        console.error('错误状态码:', error.response.status);
        console.error('错误数据:', error.response.data);
      }
      return false;
    }
  };

  // 处理导入学生
  const handleImportStudents = async () => {
    if (selectedStudentIds.length === 0) {
      message.error('请选择至少一名学生');
      return;
    }
    
    try {
      setImportLoading(true);
      
      // 逐个添加学生
      for (const studentId of selectedStudentIds) {
        await addStudentToTeacher(studentId); // 传null表示使用当前教师ID
      }
      
      message.success(`成功导入 ${selectedStudentIds.length} 名学生`);
      setImportModalVisible(false);
      fetchStudents();
    } catch (error) {
      console.error('导入学生失败:', error);
      message.error('导入学生失败');
    } finally {
      setImportLoading(false);
    }
  };
  
  // 处理学生选择变化
  const handleStudentSelectionChange = (selectedRowKeys) => {
    setSelectedStudentIds(selectedRowKeys);
  };

  // 过滤学生列表
  const filteredStudents = Array.isArray(students) ? students.filter(student => 
    student.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    student.username?.toLowerCase().includes(searchText.toLowerCase()) ||
    (student.email && student.email.toLowerCase().includes(searchText.toLowerCase()))
  ) : [];

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '年级',
      dataIndex: 'grade_level',
      key: 'grade_level',
      render: (text) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        isActive ? 
          <Tag color="green">已激活</Tag> : 
          <Tag color="red">未激活</Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? new Date(text).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个学生吗?"
            onConfirm={() => handleDeleteStudent(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 定义可用学生列表的列
  const availableStudentsColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '年级',
      dataIndex: 'grade_level',
      key: 'grade_level',
      render: (text) => text || '-',
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>学生管理</Title>
            <Text type="secondary">您可以管理属于您的学生。每个学生只能由其关联的教师管理。</Text>
          </Col>
          <Col flex="auto">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Input
                placeholder="搜索学生"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 250, marginRight: 16 }}
                prefix={<SearchOutlined />}
                allowClear
              />
              <Space>
                <Button 
                  icon={<UsergroupAddOutlined />} 
                  onClick={showImportModal}
                >
                  添加已有学生
                </Button>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={showAddModal}
                >
                  创建新学生
                </Button>
              </Space>
            </div>
          </Col>
        </Row>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredStudents}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50'],
          }}
        />
      </Card>

      {/* 添加/编辑学生模态框 */}
      <Modal
        title={editingStudent ? '编辑学生' : '添加学生'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        maskClosable={false}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddOrUpdateStudent}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入用户名" />
          </Form.Item>

          {!editingStudent && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item
            name="grade_level"
            label="年级"
            rules={[{ required: true, message: '请选择年级' }]}
          >
            <Select placeholder="请选择年级">
              {gradeOptions.map(grade => (
                <Option key={grade} value={grade}>{grade}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
            initialValue="1"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="1">激活</Option>
              <Option value="0">禁用</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                {editingStudent ? '更新' : '添加'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入已有学生模态框 */}
      <Modal
        title="添加已有学生"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            取消
          </Button>,
          <Button 
            key="import" 
            type="primary" 
            onClick={handleImportStudents} 
            loading={importLoading}
            disabled={selectedStudentIds.length === 0}
          >
            添加所选学生
          </Button>
        ]}
        width={800}
      >
        <Paragraph>
          选择要添加到您名下的学生：
        </Paragraph>
        
        <Table
          rowSelection={{
            type: 'checkbox',
            onChange: handleStudentSelectionChange
          }}
          columns={availableStudentsColumns}
          dataSource={availableStudents}
          rowKey="id"
          loading={loadingAvailable}
          pagination={{
            defaultPageSize: 5,
            showSizeChanger: true,
            pageSizeOptions: ['5', '10', '20'],
          }}
        />
      </Modal>
    </div>
  );
};

export default StudentManager; 